"use client";

import { useMemo } from "react";
import { useNoteLabel } from "@/hooks/use-notation";
import { Note } from "@/lib/music/note";
import { AZERTY_LABELS } from "@/lib/virtuosa-layout/labels";
import type {
  ChordPlacement,
  LayoutCode,
  LayoutKey,
  VirtuosaLayout,
} from "@/lib/virtuosa-layout/types";

// The tone a cell is rendered in. It answers "what is this key doing", and
// deliberately not "is it also pointed at" — that is `isHint`, carried
// alongside, because the two used to be one enum and the hint won: the moment
// a chord was pointed at, the left/right hand colouring vanished while the
// legend below kept announcing it.
export type KeyTone =
  | "idle"
  | "pressed"
  | "pressed-echo"
  | "highlighted-left"
  | "highlighted-right";

export type LabelMode = "key-and-note" | "key" | "none";

export interface VirtuosaChordKeyboardProps {
  layout: VirtuosaLayout;
  highlight?: ChordPlacement | null;
  pressed?: ReadonlySet<LayoutCode>;
  hintCodes?: ReadonlySet<LayoutCode>;
  /**
   * Cells that hold the right note but are not the one meant.
   *
   * The lattice repeats nine notes across rows 0 and 3, so a single MIDI can
   * light two cells and a three-note chord can light six. For someone learning
   * a shape that is destructive: the shape they are trying to hold is not the
   * shape on screen. Codes listed here render as a hollow echo — same note,
   * other place — so the shape stays readable while the alternative stays
   * findable.
   */
  echoCodes?: ReadonlySet<LayoutCode>;
  /**
   * Cells the current instruction refers to without asking for them — the key
   * a relative move starts from ("depuis f, une colonne à droite"), or the one
   * that just moved. Without a "you are here", a relative instruction has no
   * anchor and reads as an absolute one.
   */
  anchorCodes?: ReadonlySet<LayoutCode>;
  pathOrder?: ReadonlyMap<LayoutCode, number>;
  labels?: Record<LayoutCode, string>;
  /**
   * How much text each cell carries. Naming every cell teaches lookup — "where
   * is F♯" — which never scales to playing tempo; the layout is meant to be
   * navigated by relation, "one column right". The levels exist so the aid can
   * be taken away as it stops being needed.
   */
  labelMode?: LabelMode;
  /** Wash each cell in the colour of its pitch-class family (see FAMILY_TINT). */
  showFamilies?: boolean;
  /** Ring every C, the one anchor a player can count octaves from. */
  showOctaveAnchors?: boolean;
  showBassMarker?: boolean;
  showRootMarker?: boolean;
  onKeyDown?: (key: LayoutKey) => void;
  onKeyUp?: (key: LayoutKey) => void;
  scale?: number;
  rootPitchClass?: number;
  className?: string;
}

// Column pitch, row pitch, and the gap that keeps cells from touching.
//
// ROW_PX used to be 48 against a CELL_PX of 56, which drew the cells
// overlapping vertically and touching horizontally: the honeycomb read as one
// filled block, and a shape drawn on it had no negative space to be a shape
// against. Equal pitches also match a real keyboard, whose rows are as tall as
// its keys are wide.
const CELL_PX = 56;
const ROW_PX = 56;
const CELL_GAP = 6;
const X_PAD = 8;
const Y_PAD = 8;

// The render stagger is deliberately the uniform 0.5-per-row of the lattice,
// not the 0, 0.5, 0.75, 1.25 of a physical ANSI keyboard.
//
// The uniform figure is what `LayoutKey.x` is built from, what `handStretch`
// scores, and what MAX_HAND_STRETCH = 3.7 was calibrated against; drawing one
// geometry while scoring another would put the picture and the numbers at
// odds. It also preserves the property the whole lattice was bought for: a
// chord shape drawn here is the same drawing in all twelve keys. Under the
// physical stagger it is two drawings, because rows 2 and 3 sit a quarter key
// further left than the model assumes.
const RENDER_STAGGER = [0, 0.5, 1.0, 1.5] as const;

// Each row of the lattice is a diminished seventh, and there are only three of
// them: a note's row is fixed by its pitch class mod 3. Rows 0 and 3 are the
// same family, which is exactly why nine notes have two keys. Colouring by
// family says all of that without a sentence.
const FAMILY_TINT = [
  "rgba(125, 211, 252, 0.09)", // pc % 3 == 0 → rows 0 and 3: C, D♯, F♯, A
  "rgba(196, 181, 253, 0.09)", // pc % 3 == 1 → row 2: C♯, E, G, A♯
  "rgba(134, 239, 172, 0.09)", // pc % 3 == 2 → row 1: D, F, G♯, B
] as const;

function familyOf(midi: number): number {
  return (((midi % 12) + 12) % 12) % 3;
}

function toneOf(
  key: LayoutKey,
  highlight: ChordPlacement | null | undefined,
  pressed: ReadonlySet<LayoutCode> | undefined,
  echoCodes: ReadonlySet<LayoutCode> | undefined,
): KeyTone {
  if (pressed?.has(key.code)) {
    return echoCodes?.has(key.code) ? "pressed-echo" : "pressed";
  }

  if (highlight?.left.some((k) => k.code === key.code)) {
    return "highlighted-left";
  }

  if (highlight?.right.some((k) => k.code === key.code)) {
    return "highlighted-right";
  }

  return "idle";
}

function toneClassNames(tone: KeyTone): string {
  if (tone === "pressed") {
    return "bg-accent text-white border-accent-bright shadow-[0_0_0_2px_var(--color-accent-bright)] scale-95";
  }

  if (tone === "pressed-echo") {
    return "bg-transparent text-accent-bright/70 border-accent-bright/50 border-dashed";
  }

  if (tone === "highlighted-left") {
    return "bg-[color:var(--color-hand-left)]/30 text-[color:var(--color-hand-left)] border-[color:var(--color-hand-left)]/60";
  }

  if (tone === "highlighted-right") {
    return "bg-[color:var(--color-hand-right)]/30 text-[color:var(--color-hand-right)] border-[color:var(--color-hand-right)]/60";
  }

  return "text-text-secondary border-border-subtle hover:bg-bg-hover hover:text-text-primary";
}

// The hint ring is drawn *over* the tone rather than instead of it, so a chord
// that is both pointed at and split across hands still says which hand.
function hintClassNames(isEcho: boolean): string {
  if (isEcho) {
    return "border-amber-300/45 border-dashed shadow-[0_0_0_1px_rgba(252,211,77,0.25)]";
  }

  return "border-amber-300/80 shadow-[0_0_0_2px_rgba(252,211,77,0.45)] motion-safe:animate-pulse";
}

function noteName(midi: number): string {
  return Note.fromMidi(midi).toName({ preferSharps: true, withOctave: true });
}

export function VirtuosaChordKeyboard({
  layout,
  highlight,
  pressed,
  hintCodes,
  echoCodes,
  anchorCodes,
  pathOrder,
  labels,
  labelMode = "key-and-note",
  showFamilies = false,
  showOctaveAnchors = false,
  showBassMarker = true,
  showRootMarker = true,
  onKeyDown,
  onKeyUp,
  scale = 1,
  rootPitchClass,
  className = "",
}: VirtuosaChordKeyboardProps) {
  const toNoteLabel = useNoteLabel();
  const cell = CELL_PX * scale;
  const rowH = ROW_PX * scale;
  const gap = CELL_GAP * scale;

  const cols = useMemo(
    () => Math.max(...layout.rows.map((r) => r.length)),
    [layout],
  );

  const width = (cols + 0.5) * cell + X_PAD * 2;
  const height = layout.rows.length * rowH + Y_PAD * 2;

  const bassCode: LayoutCode | null = useMemo(() => {
    if (!highlight?.hasBass || highlight.left.length === 0) {
      return null;
    }

    let bass = highlight.left[0];
    for (const k of highlight.left) {
      if (k.midi < bass.midi) {
        bass = k;
      }
    }

    return bass.code;
  }, [highlight]);

  const labelFor = (code: LayoutCode): string =>
    labels?.[code] ?? AZERTY_LABELS[code] ?? code;

  const interactive = onKeyDown !== undefined || onKeyUp !== undefined;

  return (
    <div
      className={`relative inline-block ${className}`}
      style={{ width, height }}
    >
      {layout.rows.map((row) =>
        row.map((key) => {
          const tone = toneOf(key, highlight, pressed, echoCodes);
          const isHint = hintCodes?.has(key.code) ?? false;
          const isEcho = echoCodes?.has(key.code) ?? false;
          const isInstructionAnchor = anchorCodes?.has(key.code) ?? false;
          const x = key.col + RENDER_STAGGER[key.row];
          const left = X_PAD + x * cell + gap / 2;
          const top = Y_PAD + key.row * rowH + gap / 2;
          const isBass = bassCode === key.code;
          const isRoot =
            rootPitchClass !== undefined && key.midi % 12 === rootPitchClass;
          const isOctaveAnchor = showOctaveAnchors && key.midi % 12 === 0;
          const label = labelFor(key.code);
          const stepNumber = pathOrder?.get(key.code);
          const tinted =
            showFamilies && tone === "idle" && !isHint && !isInstructionAnchor;

          return (
            <button
              key={key.code}
              type="button"
              disabled={!interactive}
              onPointerDown={() => onKeyDown?.(key)}
              onPointerUp={() => onKeyUp?.(key)}
              onPointerLeave={() => onKeyUp?.(key)}
              className={`absolute flex flex-col items-center justify-center rounded-full border font-mono text-xs transition-all duration-100 select-none ${
                interactive ? "cursor-pointer" : "cursor-default"
              } ${toneClassNames(tone)} ${isHint ? hintClassNames(isEcho) : ""} ${
                isInstructionAnchor
                  ? "border-text-primary/50 border-dashed shadow-[0_0_0_2px_var(--color-bg-hover)]"
                  : ""
              } ${
                isOctaveAnchor && !isHint && !isInstructionAnchor
                  ? "ring-1 ring-inset ring-text-muted/40"
                  : ""
              }`}
              style={{
                width: cell - gap,
                height: cell - gap,
                left,
                top,
                backgroundColor: tinted
                  ? FAMILY_TINT[familyOf(key.midi)]
                  : undefined,
              }}
              aria-label={`${label} = ${toNoteLabel(noteName(key.midi))}`}
            >
              {labelMode !== "none" ? (
                <span className="text-sm font-semibold leading-none">
                  {label}
                </span>
              ) : null}

              {labelMode === "key-and-note" ? (
                <span className="mt-0.5 text-[10px] leading-none opacity-70">
                  {toNoteLabel(noteName(key.midi))}
                </span>
              ) : null}

              {showBassMarker && isBass ? (
                <span className="absolute -top-1 -right-1 flex h-4 w-4 items-center justify-center rounded-full bg-accent text-[9px] font-bold text-white">
                  B
                </span>
              ) : null}

              {stepNumber !== undefined ? (
                <span className="absolute -top-1 -left-1 flex h-4 w-4 items-center justify-center rounded-full bg-amber-300 text-[9px] font-bold text-neutral-900">
                  {stepNumber}
                </span>
              ) : null}

              {/* The root of a shape is the cell every other one is counted
                  from, so it gets a ring around the whole key rather than the
                  4 px dot it used to get, which was invisible and only drawn
                  when the chord happened to be highlighted. */}
              {showRootMarker && isRoot && (isHint || tone !== "idle") ? (
                <span className="pointer-events-none absolute inset-[-3px] rounded-full ring-2 ring-amber-300/90" />
              ) : null}
            </button>
          );
        }),
      )}
    </div>
  );
}
